<template>
  <div>
    <div v-if="!organizationId" class="bg-white dark:bg-[#0c1427] p-5 rounded-md shadow-md flex flex-col min-h-[450px] items-center justify-center">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500 mb-4"></div>
      <p class="text-gray-500 dark:text-gray-400">{{ directT('common.loadingOrganizationSettings') }}</p>
    </div>
    
    <CustomFieldsPanel 
      v-if="organizationId"
      :organizationId="organizationId" 
      @field-saved="handleFieldEdit"
      @field-deleted="handleFieldDelete"
    />
    
    <CustomFieldEditor
      :visible="showFieldEditor"
      :field="editingField"
      :editMode="isEditMode"
      :existingKeys="[]"
      @close="closeEditor"
      @save="saveField"
    />
    
    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="fixed inset-0 bg-black opacity-50" @click="closeDeleteModal"></div>
      <div class="bg-white dark:bg-[#0c1427] p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-md mx-4 z-10 relative">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-medium text-black dark:text-white">{{ directT('settings.leadsSettings.customFields.confirmDeletion') }}</h3>
          <button 
            @click="closeDeleteModal" 
            class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 -m-2"
          >
            <i class="material-symbols-outlined text-xl">close</i>
          </button>
        </div>
        
        <div class="mb-6">
          <p class="text-gray-700 dark:text-gray-300">
            {{ directT('confirmDeletionMessage', { fieldName: fieldToDelete?.label }) }}
          </p>
          <p class="text-gray-500 text-sm mt-2">
            {{ directT('confirmDeletionWarning') }}
          </p>
        </div>
        
        <div class="flex justify-between pt-2">
          <button 
            type="button" 
            @click="closeDeleteModal"
            class="inline-block py-2 px-4 bg-gray-100 text-gray-800 transition-all hover:bg-gray-200 rounded-md border border-gray-100 hover:border-gray-200 dark:bg-[#172036] dark:text-gray-200 dark:hover:bg-[#1e2746] dark:border-[#172036] dark:hover:border-[#1e2746]"
          >
            {{ directT('settings.leadsSettings.delete.cancel') }}
          </button>
          <button 
            type="button"
            @click="confirmDelete"
            class="inline-block py-2 px-4 bg-danger-500 text-white transition-all hover:bg-danger-400 rounded-md border border-danger-500 hover:border-danger-400"
          >
            {{ directT('settings.leadsSettings.delete.confirm') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, PropType } from "vue";
import { supabase } from "@/lib/supabaseClient";
import { createToast } from "@/utils/toast";
import { getCurrentOrgId } from "@/utils/getCurrentOrgId";
import CustomFieldsPanel, { CustomField } from "./CustomFieldsPanel.vue";
import CustomFieldEditor from "./CustomFieldEditor.vue";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for translation objects
type TranslationObject = Record<string, any>;

// Translation files
const translationFiles = {
  en: enTranslations,
  he: heTranslations,
  ar: arTranslations
};

export default defineComponent({
  name: "CustomFields",
  components: {
    CustomFieldsPanel,
    CustomFieldEditor
  },
  emits: ["save-field", "delete-field"],
  setup(props, { emit }) {
    const organizationId = ref("");
    
    // i18n setup
    const { locale } = useI18n();
    
    // Direct translation function that bypasses vue-i18n
    const directT = (key: string, params: Record<string, any> = {}): string => {
      // First try to get the translation using the exact key
      const parts = key.split('.');
      
      // Normalize keys that don't have a prefix
      if (!key.includes('.')) {
        key = `settings.leadsSettings.customFields.${key}`;
      } 
      // Normalize keys that start with 'settings.leadsSettings'
      else if (key.startsWith('settings.leadsSettings')) {
        key = key.replace('settings.leadsSettings', 'settings.leadsSettings');
      }
      
      const normalizedParts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of normalizedParts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of normalizedParts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      
      if (typeof result !== 'string') {
        console.warn(`Translation key ${key} does not resolve to a string`);
        return key;
      }
      
      // Replace parameters in the string if any
      if (Object.keys(params).length > 0) {
        let finalResult: string = result;
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          finalResult = finalResult.replace(new RegExp(`{${paramKey}}`, 'g'), String(paramValue));
        });
        return finalResult;
      }
      
      return result;
    };
    
    // Field editor state
    const showFieldEditor = ref(false);
    const editingField = ref<CustomField | null>(null);
    const isEditMode = ref(false);
    const editingIndex = ref(-1);
    
    // Delete confirmation
    const showDeleteModal = ref(false);
    const fieldToDelete = ref<CustomField | null>(null);
    const deleteIndex = ref(-1);
    
    // Get organization ID on mount
    onMounted(async () => {
      try {
        const orgId = await getCurrentOrgId();
        if (orgId) {
          organizationId.value = orgId;
        } else {
          createToast('User is not associated with an organization', 'error');
        }
      } catch (error) {
        console.error('Error getting organization ID', error);
        createToast('Failed to load organization information', 'error');
      }
    });
    
    // Handle editing a field
    const handleFieldEdit = (data: { action: 'add' | 'edit', field: CustomField, index?: number }) => {
      editingField.value = data.field;
      isEditMode.value = data.action === 'edit';
      editingIndex.value = data.index !== undefined ? data.index : -1;
      showFieldEditor.value = true;
    };
    
    // Close the field editor
    const closeEditor = () => {
      showFieldEditor.value = false;
      editingField.value = null;
      isEditMode.value = false;
      editingIndex.value = -1;
    };
    
    // Save a field (after editor completes)
    const saveField = (field: CustomField) => {
      // Update the CustomFieldsPanel properly by emitting events
      const panel = document.querySelector('.custom-fields-panel');
      if (panel) {
        // Using a safer approach that doesn't rely on internal Vue properties
        emit('save-field', {
          field,
          index: isEditMode.value ? editingIndex.value : -1
        });
        
        if (isEditMode.value && editingIndex.value !== -1) {
          // Clear edit mode after saving
          isEditMode.value = false;
          editingIndex.value = -1;
        }
      }
      
      // Close the editor
      closeEditor();
      createToast('Field saved successfully', 'success');
    };
    
    // Handle deleting a field
    const handleFieldDelete = (data: { field: CustomField, index: number }) => {
      fieldToDelete.value = data.field;
      deleteIndex.value = data.index;
      showDeleteModal.value = true;
    };
    
    // Close the delete modal
    const closeDeleteModal = () => {
      showDeleteModal.value = false;
      fieldToDelete.value = null;
      deleteIndex.value = -1;
    };
    
    // Confirm field deletion
    const confirmDelete = () => {
      // Similar issue with accessing the panel directly
      const panel = document.querySelector('.custom-fields-panel');
      if (panel && deleteIndex.value !== -1) {
        // Using a safer approach that doesn't rely on internal Vue properties
        emit('delete-field', deleteIndex.value);
        
        // Show success message
        createToast('Field deleted successfully', 'success');
      }
      
      // Close the modal
      closeDeleteModal();
    };
    
    return {
      organizationId,
      showFieldEditor,
      editingField,
      isEditMode,
      showDeleteModal,
      fieldToDelete,
      
      handleFieldEdit,
      closeEditor,
      saveField,
      handleFieldDelete,
      closeDeleteModal,
      confirmDelete,
      directT
    };
  }
});
</script> 